import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 md:pt-32 md:pb-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-6 animate-fade-in">
              Get In Touch
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-6 animate-fade-in-up">
              <span className="gold-gradient">Contact</span>
              <span className="text-white/90"> Us</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 font-light leading-relaxed max-w-xl mx-auto animate-fade-in-up-delay-1">
              Have questions about our Goldnotes? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="pb-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 max-w-6xl mx-auto">
            
            {/* Contact Info */}
            <div>
              <div className="mb-12">
                <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-4">
                  Reach Out
                </p>
                <h2 className="text-2xl md:text-3xl text-white/90 mb-6" style={{ fontFamily: 'Cinzel, serif' }}>
                  We're Here to Help
                </h2>
                <p className="text-sm text-white/70 font-light leading-relaxed">
                  Whether you have questions about our products, need assistance with an order, 
                  or want to explore partnership opportunities, our team is ready to assist you.
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-8">
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 shrink-0 border border-[#D4AF37]/30 flex items-center justify-center rounded-lg">
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm tracking-wider uppercase text-white/70 mb-2">Email</h3>
                    <a href="mailto:gold@goldnote.gold" className="text-[#D4AF37] hover:text-[#EEDC9A] transition-colors">
                      gold@goldnote.gold
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 shrink-0 border border-[#D4AF37]/30 flex items-center justify-center rounded-lg">
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm tracking-wider uppercase text-white/70 mb-2">Phone</h3>
                    <a href="tel:1-855-558-4653" className="text-white/80 hover:text-white transition-colors">
                      1-855-558-GOLD (4653)
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 shrink-0 border border-[#D4AF37]/30 flex items-center justify-center rounded-lg">
                    <svg className="w-5 h-5 text-[#D4AF37]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm tracking-wider uppercase text-white/70 mb-2">Headquarters</h3>
                    <p className="text-white/80 text-sm leading-relaxed">
                      100 Yellow Brick Road<br />
                      Niagara Falls, Ontario<br />
                      Canada
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-12 pt-8 border-t border-white/5">
                <p className="text-[0.6rem] tracking-[0.2em] uppercase text-white/40 mb-4">Follow Us</p>
                <div className="flex gap-4">
                  <a href="#" className="w-10 h-10 border border-white/10 hover:border-[#D4AF37]/50 flex items-center justify-center rounded-lg transition-colors group">
                    <svg className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                    </svg>
                  </a>
                  <a href="#" className="w-10 h-10 border border-white/10 hover:border-[#D4AF37]/50 flex items-center justify-center rounded-lg transition-colors group">
                    <svg className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a href="#" className="w-10 h-10 border border-white/10 hover:border-[#D4AF37]/50 flex items-center justify-center rounded-lg transition-colors group">
                    <svg className="w-4 h-4 text-white/40 group-hover:text-[#D4AF37] transition-colors" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="p-8 bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl">
                <div className="mb-8">
                  <h3 className="font-serif text-xl text-white/90 mb-2">Send a Message</h3>
                  <p className="text-xs text-white/40">We typically respond within 24 hours.</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 h-11"
                        placeholder="Your name"
                        data-testid="input-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 h-11"
                        placeholder="email@example.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                      Subject *
                    </Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 h-11"
                      placeholder="How can we help?"
                      data-testid="input-subject"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[0.6rem] tracking-[0.12em] uppercase text-white/40">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="bg-[#121212] border-white/10 text-white placeholder:text-white/20 focus:border-[#D4AF37]/50 resize-none"
                      placeholder="Tell us more about your inquiry..."
                      data-testid="input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#D4AF37] text-[#050505] hover:bg-[#EEDC9A] font-sans text-xs tracking-[0.15em] uppercase py-6 rounded-lg"
                    data-testid="submit-contact-btn"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-[0.625rem] tracking-[0.4em] uppercase text-[#D4AF37]/80 mb-4">
                Common Questions
              </p>
              <h2 className="text-2xl md:text-3xl text-white/90" style={{ fontFamily: 'Cinzel, serif' }}>
                Frequently Asked
              </h2>
            </div>

            <div className="space-y-4">
              <div className="p-6 bg-[#050505] border border-white/5 rounded-lg">
                <h3 className="text-white/90 mb-2">How do I verify authenticity of my Goldnote?</h3>
                <p className="text-sm text-white/70 font-light">
                  Each Goldnote comes with a unique QR code for instant verification. 
                  Scan the QR code with your smartphone to verify your note on our secure blockchain registry.
                </p>
              </div>

              <div className="p-6 bg-[#050505] border border-white/5 rounded-lg">
                <h3 className="text-white/90 mb-2">Where can I purchase Goldnotes?</h3>
                <p className="text-sm text-white/70 font-light">
                  Goldnotes are available through our network of authorized retailers. 
                  Visit our homepage and select your region to find a retailer near you.
                </p>
              </div>

              <div className="p-6 bg-[#050505] border border-white/5 rounded-lg">
                <h3 className="text-white/90 mb-2">What is the minimum order for corporate notes?</h3>
                <p className="text-sm text-white/70 font-light">
                  Corporate custom notes typically start at a minimum of 25 units. 
                  Contact our corporate team for detailed pricing and customization options.
                </p>
              </div>

              <div className="p-6 bg-[#050505] border border-white/5 rounded-lg">
                <h3 className="text-white/90 mb-2">How is the gold secured within the note?</h3>
                <p className="text-sm text-white/70 font-light">
                  Our patented technology (U.S. Patent No. 10,668,765) securely embeds 99.99% pure gold 
                  within a durable polymer substrate, protecting your investment while maintaining its beauty.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
